import {
  MemberStatusChangeRequestAuditForm,
  MemberStatusChangeRequestDto,
  MemberStatusChangeRequestForm,
  MemberStatusDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { isOutOfScopeStatusChangeRestricted } from '@act/shared/types';
import { SelectOption } from '@act/storybook';
import { Alert, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import HtmlDisplayer from 'react-components/components/HtmlDisplayer';
import {
  FormConfig,
  FormItem,
  FormPassthrough,
} from 'stories/forms/FormBuilder/formBuilder.types';
import {
  canApproveStatusChangeRequest,
  getStatusChangeRequestDateRestrictionDescription,
  isInGracePeriodForStatusChangeRequest,
} from 'utilities/date-validation/statusChangeRequestDates.util';

interface FormConfigParams {
  isAuditingRequest: boolean;
  selectOptions: SelectOption<number>[];
  selectedOption: MemberStatusDto;
  minDate: DateTime;
  member: UserDto;
  author: UserDto;
  memberStatusChangeRequest: MemberStatusChangeRequestDto;
  currentEffectiveAt: string;
}

export const createMemberStatusChangeRequestFormConfig = ({
  isAuditingRequest,
  selectOptions,
  selectedOption,
  minDate,
  member,
  author,
  memberStatusChangeRequest,
  currentEffectiveAt,
}: FormConfigParams): FormConfig<
  MemberStatusChangeRequestForm | MemberStatusChangeRequestAuditForm
> => {
  const isStatusChangeRestricted =
    !isAuditingRequest &&
    selectOptions.length === 0 &&
    isOutOfScopeStatusChangeRestricted(member?.memberStatus?.key);

  return [
    // Request form fields (shown when not auditing)
    ...(!isAuditingRequest
      ? [
          isStatusChangeRestricted &&
            ({
              type: 'passthrough',
              content: (
                <Alert
                  severity="info"
                  className="member-status-change-request-alert"
                >
                  Only an administrator can request a status change while this
                  member is Out of Scope.
                </Alert>
              ),
            } as FormPassthrough),
          {
            type: 'input',
            name: 'memberStatusId',
            label: 'What is the reason for change of status?',
            input: 'select',
            disabled: isStatusChangeRestricted,
            props: {
              options: selectOptions,
              required: true,
              fullWidth: true,
              trackingName: 'member-status-select',
            },
          } as FormItem<MemberStatusChangeRequestForm>,
          // Combined info alert for status requirements and date restrictions
          (selectedOption && selectedOption.changeRequestDescription) ||
          isInGracePeriodForStatusChangeRequest()
            ? ({
                type: 'passthrough',
                content: (
                  <Alert
                    severity="info"
                    className="member-status-change-request-alert"
                  >
                    <div>
                      {selectedOption &&
                        selectedOption.changeRequestDescription && (
                          <div className="member-status-change-selected-option">
                            <h6>{selectedOption.name} status requirement</h6>
                            <HtmlDisplayer
                              className="description"
                              innerHtml={
                                selectedOption.changeRequestDescription
                              }
                            />
                          </div>
                        )}
                      {isInGracePeriodForStatusChangeRequest() && (
                        <>
                          {selectedOption &&
                            selectedOption.changeRequestDescription && (
                              <hr className="member-status-change-alert-divider" />
                            )}
                          <Typography variant="body2">
                            {getStatusChangeRequestDateRestrictionDescription()}
                          </Typography>
                        </>
                      )}
                    </div>
                  </Alert>
                ),
              } as FormPassthrough)
            : null,
          selectedOption &&
            ({
              type: 'input',
              name: 'effectiveAt',
              label:
                selectedOption?.effectiveAtQuestionText ||
                'When should this status change occur?',
              input: 'date',
              props: {
                required: true,
                minDate: minDate,
                trackingName: 'effective-at-date-input',
              },
            } as FormItem<MemberStatusChangeRequestForm>),
          selectedOption &&
            ({
              type: 'input',
              name: 'creatorComment',
              label: 'Leave a comment to help the team process your request.',
              input: 'text',
              props: {
                maxLength: 500,
                multiline: true,
                rows: 3,
                trackingName: 'creator-comment-input',
              },
            } as FormItem<MemberStatusChangeRequestForm>),
        ]
      : []),
    // Audit form fields (shown when auditing)
    ...(isAuditingRequest
      ? [
          {
            type: 'passthrough',
            content: (
              <div className="info-pane">
                <h6>Member</h6>
                <p>
                  {member.firstName} {member.lastName}
                </p>
                {selectedOption && (
                  <>
                    <h6>Reason for Change Request</h6>
                    <p>{selectedOption.name}</p>
                  </>
                )}
                <h6>Requesting Guide</h6>
                <p>{author?.name}</p>
                <h6>Comments</h6>
                {memberStatusChangeRequest.creatorComment}
              </div>
            ),
          } as FormPassthrough,
          // Info alert about date restrictions for auditing (only show when in grace period)
          isInGracePeriodForStatusChangeRequest() &&
            ({
              type: 'passthrough',
              content: (
                <Alert
                  severity="info"
                  className="member-status-change-request-alert"
                >
                  <Typography variant="body2">
                    {getStatusChangeRequestDateRestrictionDescription()}
                  </Typography>
                </Alert>
              ),
            } as FormPassthrough),
          // Warning alert if request can no longer be approved due to grace period expiration
          // Show this warning whenever auditing a request that can't be approved
          !canApproveStatusChangeRequest(
            currentEffectiveAt || memberStatusChangeRequest.effectiveAt
          ) &&
            ({
              type: 'passthrough',
              content: (
                <Alert
                  severity="warning"
                  className="member-status-change-request-warning"
                >
                  <Typography variant="body2">
                    This request is for a date that is no longer allowed for
                    status change requests. The grace period for previous month
                    changes has elapsed.
                  </Typography>
                </Alert>
              ),
            } as FormPassthrough),
          {
            type: 'input',
            name: 'effectiveAt',
      trackingName: 'effective-at-input',
            label:
              selectedOption?.effectiveAtQuestionText ||
              'When should this status change occur?',
            input: 'date',
            props: {
              required: true,
              minDate: minDate,
            },
          } as FormItem<MemberStatusChangeRequestAuditForm>,
          {
            type: 'input',
            name: 'approval',
            label: 'Member status change approval/denial',
            input: 'radioGroup',
            props: {
              options: [
                {
                  label: 'Approved',
                  value: 'approve',
                  disabled: !canApproveStatusChangeRequest(
                    currentEffectiveAt || memberStatusChangeRequest.effectiveAt
                  ),
                },
                { label: 'Denied', value: 'deny' },
              ],
              trackingName: 'approval-radio-group',
            },
          } as FormItem<MemberStatusChangeRequestAuditForm>,
          {
            type: 'input',
            name: 'auditorComment',
            label: 'Explain the reason(s) for the decision.',
            input: 'text',
            props: {
              maxLength: 500,
              multiline: true,
              rows: 3,
              trackingName: 'auditor-comment-input',
            },
          } as FormItem<MemberStatusChangeRequestAuditForm>,
        ]
      : []),
  ].filter(Boolean);
};
